import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Settings, RefreshCw } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const MechanicalTransmission = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/mechanical-transmission"]?.title} description={seoData["services/mechanical-transmission"]?.description} path="/services/mechanical-transmission" keywords={seoData["services/mechanical-transmission"]?.keywords || []} />

      <PageHero
        badge="Machinery & Equipment"
        title="Mechanical & Power Transmission Repair"
        description="Mechanical transmission and power system repair contractor. Shaft rebuilding, keyway repair, bearing seat restoration, and coupling repair using cold-applied Belzona epoxy composites. No welding, no machining."
        backgroundKey="mechanical-transmission"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Shaft wear, scoring, and journal damage',
              'Keyway damage and wallowed key seats',
              'Bearing seat wear and loss of interference fit',
              'Coupling hub wear and misalignment damage',
              'Gearbox shaft and gear tooth damage',
              'Spline wear and fretting corrosion'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-zinc-50 rounded-lg">
                <Settings className="text-zinc-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-zinc-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <RefreshCw className="text-zinc-600 mr-3 flex-shrink-0" size={22} />
                  Shaft Repair & Rebuilding
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Shaft journal repair, scoring repair, and diameter restoration using Belzona metal repair composites. Return shafts to OEM dimensions without welding or machining.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-zinc-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <RefreshCw className="text-zinc-600 mr-3 flex-shrink-0" size={22} />
                  Keyway & Spline Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Keyway rebuild, wallowed key seat repair, and spline restoration for pump, motor, and gearbox shafts. Precision application to restore proper fit and torque transmission.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-zinc-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <RefreshCw className="text-zinc-600 mr-3 flex-shrink-0" size={22} />
                  Bearing Seat Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Rebuild worn bearing seats to restore proper interference fit using Belzona 1321 and 1131 bearing metal composites. Eliminate bearing spin and premature failure.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-zinc-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <RefreshCw className="text-zinc-600 mr-3 flex-shrink-0" size={22} />
                  Coupling & Hub Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Coupling hub bore repair, taper fit restoration, and alignment surface rebuild for flexible and rigid coupling systems.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Cold-applied — no welding heat, no distortion, no machining required',
                  'In-situ repair on installed equipment',
                  'Restore OEM tolerances and specifications',
                  'Rapid cure — return to service in hours',
                  '70% cost savings vs. shaft or component replacement',
                  'Works on all shaft materials — carbon steel, stainless, alloy',
                  'Emergency repair for critical drive system failures',
                  'Precision measurement and documentation included'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">General shaft and component rebuild</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1131 (Bearing Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Bearing seat restoration</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Wear-resistant shaft coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1341 (Supermetalglide)</p>
                  <p className="text-xs text-slate-500 mt-1">Low-friction shaft surface coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Shaft or Drive System Damaged? We Fix It In Place.</h2>
          <p className="text-blue-100 mb-8">Contact us for mechanical repair assessment. Emergency response available for critical drive system failures.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MechanicalTransmission;
